<template>
  <div v-if="enabled" class="form-group full image-captcha">
    <div class="captcha-row">
      <input ref="answer" v-model="answer" class="form-control" type="text" inputmode="numeric"
        :placeholder="english ? 'Enter verification code' : '請輸入驗證碼'"
        :aria-label="english ? 'Enter verification code' : '請輸入驗證碼'"
        autocomplete="off" maxlength="6" pattern="[0-9]{6}" :disabled="!loaded || loading || pending" />
      <div class="captcha-picture" role="button" tabindex="0"
        :aria-label="english ? 'Click image for a new code' : '點擊圖片更換驗證碼'"
        :title="english ? 'Click image for a new code' : '點擊圖片更換驗證碼'"
        :aria-disabled="loading || pending || retrySeconds > 0"
        @click="refresh()" @keydown.enter.prevent="refresh()" @keydown.space.prevent="refresh()">
        <img v-if="image" :key="challengeId" :src="image" width="180" height="56"
          :alt="english ? 'Verification image with six digits' : '六位數字驗證圖片'"
          @load="loaded = true" @error="imageFailed" />
        <span v-else>{{ loading ? (english ? 'Loading…' : '載入中…') : (english ? 'Click to reload image' : '點此重新載入圖片') }}</span>
      </div>
    </div>
    <p v-if="error" role="status" aria-live="polite" class="captcha-error">{{ error }}</p>
    <p v-if="retrySeconds > 0" role="status">{{ english ? 'Retry in ' + retrySeconds + ' seconds.' : '請於 ' + retrySeconds + ' 秒後再試。' }}</p>
  </div>
</template>
<script>
import { captchaApiUrl } from '@/utils/captchaApi';
export default {
  props: { endpoint: { type: String, required: true }, purpose: { type: String, required: true }, english: Boolean },
  data: () => ({ challengeId: '', image: '', expiresAt: '', answer: '', error: '',
    enabled: true, loading: false, loaded: false, pending: false, retrySeconds: 0, generation: 0 }),
  mounted() { this.refresh(); },
  beforeDestroy() {
    this.generation++;
    clearInterval(this.retryTimer);
    if (this.controller) this.controller.abort();
  },
  watch: { purpose() { this.challengeId = ''; this.refresh(true); } },
  methods: {
    async refresh(force = false) {
      if (this.pending || this.retrySeconds > 0 || (this.loading && !force)) return;
      if (this.controller) this.controller.abort();
      const generation = ++this.generation;
      this.loading = true; this.loaded = false; this.answer = ''; this.image = ''; this.error = '';
      this.enabled = true;
      const replaceId = this.challengeId;
      this.challengeId = '';
      const controller = new AbortController(); this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(captchaApiUrl(this.endpoint), {
          method: 'POST', credentials: 'include', cache: 'no-store',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body: new URLSearchParams({ purpose: this.purpose, replaceId }), signal: controller.signal
        });
        const result = await response.json();
        if (generation !== this.generation) return;
        if (response.ok && result.ok === true && result.enabled === false) {
          this.enabled = false;
          return;
        }
        if (Number(result.retryAfter) > 0) {
          this.retrySeconds = Math.max(1, Number(result.retryAfter) || 30);
          this.error = typeof result.error === 'string' ? result.error :
            (this.english ? 'Please wait before requesting another image.' : '請稍後再取得驗證碼。');
          clearInterval(this.retryTimer);
          this.retryTimer = setInterval(() => {
            this.retrySeconds = Math.max(0, this.retrySeconds - 1);
            if (!this.retrySeconds) { clearInterval(this.retryTimer); this.error = ''; }
          }, 1000);
          this.challengeId = replaceId;
          return;
        }
        if (!response.ok || result.error || !/^[a-f0-9]{48}$/.test(result.challengeId) ||
          typeof result.image !== 'string' || !result.image.startsWith('data:image/png;base64,') ||
          !Number.isFinite(Date.parse(result.expiresAt))) throw new Error('image');
        this.challengeId = result.challengeId; this.image = result.image; this.expiresAt = result.expiresAt;
      } catch {
        if (generation !== this.generation) return;
        this.challengeId = replaceId;
        this.error = this.english ? 'Image unavailable. Click the image area to retry.' : '無法載入驗證圖片，請點擊圖片區域重試。';
      } finally {
        clearTimeout(timeout);
        if (generation === this.generation) this.loading = false;
      }
    },
    imageFailed() {
      this.loaded = false;
      this.error = this.english ? 'Image unavailable. Click the image to retry.' : '圖片載入失敗，請點擊圖片重試。';
    },
    take(purpose) {
      if (this.pending) return null;
      if (!this.enabled && this.purpose === purpose && !this.loading) {
        this.pending = true;
        return {};
      }
      if (this.purpose !== purpose || !this.loaded || this.loading || !this.challengeId) {
        this.error = this.english ? 'Please wait for the image or click to retry.' : '請等候圖片載入，或點擊圖片重試。';
        return null;
      }
      if (Date.parse(this.expiresAt) <= Date.now()) {
        this.refresh();
        this.error = this.english ? 'Code expired. Enter the new code.' : '驗證碼已過期，請輸入新圖片的驗證碼。';
        return null;
      }
      if (!/^[0-9]{6}$/.test(this.answer)) {
        this.error = this.english ? 'Enter the six digits shown.' : '請輸入圖片中的六位數字。';
        this.$refs.answer.focus(); return null;
      }
      this.pending = true; this.error = '';
      return { captchaId: this.challengeId, captchaAnswer: this.answer };
    },
    release() { this.pending = false; this.answer = ''; this.refresh(); }
  }
};
</script>
<style scoped>
.image-captcha { clear: both; width: 100%; margin: 16px 0; color: #172b3a; }
.captcha-row { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.image-captcha input { box-sizing: border-box; flex: 1 1 160px; min-width: 0; width: 100%; padding: 10px; border: 1px solid #777; font-size: 1.1rem; letter-spacing: .15em; }
.captcha-picture { display: flex; align-items: center; justify-content: center; min-width: 180px; min-height: 56px; cursor: pointer; }
.captcha-picture:focus-visible { outline: 2px solid #176b76; outline-offset: 2px; }
.captcha-picture[aria-disabled="true"] { cursor: wait; opacity: .6; }
.image-captcha .captcha-error { color: #a12720; margin: 8px 0; }
</style>
